import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { Info, QrCode, Power, Loader2, Copy, Check, MoreVertical, Archive, ArchiveRestore, Trash2, Eraser } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton, Spinner } from '@/components/ui/misc';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { useProjects } from '@/components/ProjectSwitcher';
import { OfficialSetup } from '@/pages/OfficialSetup';
import { makeClient, apiFetch, getProject, streamUrl } from '@/lib/api';
import { isOfficial } from '@/lib/projects';
import { track } from '@/lib/analytics';
import { formatPhone } from '@/lib/phone';
import { useI18n, useT } from '@/lib/i18n';
import { useStatusLabel } from '@/pages/Dashboard';

interface Instance {
  id: string; phone: string; nickname?: string; status: string; status_reason?: string;
  banned_until?: string | null; archived_at?: string | null;
  consecutive_send_failures?: number; last_send_error_code?: number;
}
const statusVariant = (s: string) => (s === 'connected' || s === 'warming' ? 'success' : s === 'connecting' || s === 'qr' ? 'warning' : s === 'banned' ? 'destructive' : 'secondary');
// banRemaining formata quanto falta para um ban TEMPORÁRIO expirar ("~2h", "~45 min", "~1d").
const banRemaining = (iso: string) => {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return '';
  const h = Math.floor(ms / 3_600_000);
  if (h >= 24) return `${Math.floor(h / 24)}d`;
  if (h >= 1) return `${h}h`;
  return `${Math.max(1, Math.floor(ms / 60_000))} min`;
};

// REACH_OUT_LOCK é o código 463 do WhatsApp: bloqueio anti-spam por conta que
// impede iniciar conversa com contatos frios. O número está saudável.
const REACH_OUT_LOCK = 463;

// RejectingBadge revela o estado que o status sozinho esconde: número ONLINE
// cujos envios o WhatsApp está recusando. Foi o ponto cego do incidente de
// 2026-07-08 — painel verde, cliente sem receber nada.
function RejectingBadge({ instance }: { instance: Instance }) {
  const t = useT();
  const failures = instance.consecutive_send_failures ?? 0;
  const online = instance.status === 'connected' || instance.status === 'warming';
  if (!online || failures === 0) return null;
  const hint = instance.last_send_error_code === REACH_OUT_LOCK
    ? t('numbers.rejectingReachOut')
    : t('numbers.rejectingHint', { n: failures });
  return <Badge variant="destructive" title={hint}>{t('numbers.rejecting')} ({failures})</Badge>;
}

// CopyId mostra o instance_id (dado de máquina, fonte mono) com botão de copiar — usado nos
// envios direcionados pela API (opcional; só para forçar um número específico).
interface InstanceUptime {
  windows: Record<'24h' | '7d' | '30d', number | null>;
}

// UptimeLine — quanto do tempo o número esteve no ar: o pilar de uptime pede CERTEZA de
// que o número está ligado, e o status de agora não diz se ele caiu de madrugada.
function UptimeLine({ id }: { id: string }) {
  const { t, locale } = useI18n();
  const { data } = useQuery({
    queryKey: ['uptime', id],
    staleTime: 60_000,
    queryFn: async (): Promise<InstanceUptime | null> => {
      const r = await apiFetch('GET', `/instances/${id}/uptime`);
      return r.ok ? (r.data as InstanceUptime) : null;
    },
  });
  if (!data) return null;
  const fmt = (n: number) => n.toLocaleString(locale, { maximumFractionDigits: 1 });
  const d7 = data.windows['7d'];
  const h24 = data.windows['24h'];
  return (
    <p className="w-full text-[11px] text-muted-foreground">
      {d7 == null || h24 == null ? t('numbers.uptimeStarting') : t('numbers.uptime', { d7: fmt(d7), h24: fmt(h24) })}
    </p>
  );
}

function CopyId({ id }: { id: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      title={t('numbers.copyId')}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(id);
        } catch {
          /* clipboard indisponível */
        }
        setCopied(true);
        toast.success(t('numbers.idCopied'));
        setTimeout(() => setCopied(false), 1500);
      }}
      className="mt-1 flex max-w-full items-center gap-1 font-mono text-[11px] text-muted-foreground transition-colors hover:text-foreground"
    >
      {copied ? <Check className="size-3 shrink-0 text-[#3C9A18]" /> : <Copy className="size-3 shrink-0" />}
      <span className="truncate">{id}</span>
    </button>
  );
}

/**
 * A tela de números tem duas caras, e quem decide é o trilho do projeto.
 *
 * No trilho não oficial, um número é uma sessão pareada por QR — a lista, o
 * diálogo do QR e o ciclo conectar/desconectar abaixo. No oficial não existe
 * nada disso: o número pertence à conta comercial do cliente na Meta, e o que
 * há para fazer é o onboarding. Por isso a escolha vive num componente
 * separado: assim nenhum dos dois lados carrega estado do outro.
 */
export function Numbers() {
  const { data: projects = [] } = useProjects();
  const active = projects.find((p) => p.id === getProject()) ?? projects.find((p) => p.is_default);
  return isOfficial(active) ? <OfficialSetup /> : <UnofficialNumbers />;
}

function UnofficialNumbers() {
  const t = useT();
  const statusLabel = useStatusLabel();
  const qc = useQueryClient();
  const { data: list, isLoading } = useQuery({
    queryKey: ['instances'],
    queryFn: async (): Promise<Instance[]> =>
      ((await makeClient().GET('/instances')).data as { data?: Instance[] })?.data ?? [],
    refetchInterval: 8000,
  });
  // Números arquivados (inativos, reativáveis) — seção separada.
  const { data: archived } = useQuery({
    queryKey: ['instances', 'archived'],
    queryFn: async (): Promise<Instance[]> => ((await apiFetch('GET', '/instances?archived=1')).data as { data?: Instance[] })?.data ?? [],
  });

  const [connectId, setConnectId] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Instance | null>(null); // número no diálogo de remoção

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['instances'] });
    qc.invalidateQueries({ queryKey: ['instances', 'archived'] });
  };

  const disconnect = async (id: string) => {
    const { error } = await makeClient().POST('/instances/{id}/disconnect', { params: { path: { id } } });
    if (error) toast.error(t('numbers.disconnectFailed')); else { toast.success(t('numbers.disconnectedToast')); refresh(); }
  };

  const archive = async (i: Instance) => {
    const r = await apiFetch('POST', `/instances/${i.id}/archive`);
    if (!r.ok) { toast.error(t('numbers.archiveFailed')); return; }
    toast.success(t('numbers.archivedToast')); setRemoving(null); refresh();
  };
  const unarchive = async (i: Instance) => {
    const r = await apiFetch('POST', `/instances/${i.id}/unarchive`);
    if (!r.ok) { toast.error(t('numbers.reactivateFailed')); return; }
    toast.success(t('numbers.reactivatedToast')); refresh();
  };
  // Limpar sessão: apaga a credencial do device pareado. É o reparo para quando
  // o /connect não emite QR (device órfão) — o logout comum não resolve.
  const [clearing, setClearing] = useState<Instance | null>(null);
  const [clearingBusy, setClearingBusy] = useState(false);
  const clearSession = async (i: Instance) => {
    setClearingBusy(true);
    try {
      const { error } = await makeClient().POST('/instances/{id}/clear-session', { params: { path: { id: i.id } } });
      if (error) { toast.error(t('numbers.clearSessionFailed')); return; }
      toast.success(t('numbers.clearSessionDone')); setClearing(null); refresh();
    } finally { setClearingBusy(false); }
  };

  const [deleting, setDeleting] = useState(false);
  const remove = async (i: Instance) => {
    setDeleting(true);
    try {
      const { error } = await makeClient().DELETE('/instances/{id}', { params: { path: { id: i.id } } });
      if (error) {
        // 409 has_history → não dá pra excluir sem perder o histórico; sugere arquivar.
        toast.error(((error as { code?: string })?.code) === 'has_history' ? t('numbers.hasHistory') : t('numbers.deleteFailed'));
        return;
      }
      toast.success(t('numbers.deletedToast')); setRemoving(null); refresh();
    } finally { setDeleting(false); }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">{t('numbers.subtitle')}</p>
      </div>
      {/* Trilho de QR fechado para números novos (a API recusa): os pareados seguem. */}
      <p className="flex gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-muted-foreground">
        <Info className="mt-0.5 size-4 shrink-0 text-primary" /> {t('numbers.closedNotice')}
      </p>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-3 p-5">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : (list ?? []).length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <QrCode className="size-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{t('numbers.empty')}</p>
            </div>
          ) : (
            <>
              {/* cabeçalho só no desktop; no mobile cada item empilha (sem cortar texto) */}
              <div className="hidden border-b px-5 py-3 text-xs font-medium uppercase tracking-wide text-muted-foreground sm:grid sm:grid-cols-[1fr_auto_auto] sm:items-center sm:gap-4">
                <span>{t('numbers.number')}</span>
                <span>{t('numbers.status')}</span>
                <span className="text-right">{t('numbers.actions')}</span>
              </div>
              <ul className="divide-y">
                {(list ?? []).map((i) => (
                  <li key={i.id} className="flex flex-col gap-2 px-5 py-3 text-sm sm:grid sm:grid-cols-[1fr_auto_auto] sm:items-center sm:gap-4">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{i.nickname || formatPhone(i.phone)}</p>
                      <p className="truncate text-xs text-muted-foreground">{formatPhone(i.phone)}</p>
                      <CopyId id={i.id} />
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant={statusVariant(i.status)}>{statusLabel(i.status)}</Badge>
                      <RejectingBadge instance={i} />
                      <UptimeLine id={i.id} />
                      {i.status === 'banned' && (
                        <p className="mt-0.5 w-full text-[11px] text-muted-foreground">
                          {i.banned_until && new Date(i.banned_until) > new Date()
                            ? `${t('numbers.banReconnectIn')} ~${banRemaining(i.banned_until)}`
                            : t('numbers.banPermanent')}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 sm:justify-end">
                      {i.status === 'connected' || i.status === 'warming' ? (
                        <Button size="sm" variant="outline" onClick={() => disconnect(i.id)}><Power className="size-3.5" /> {t('numbers.disconnect')}</Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setConnectId(i.id)}><QrCode className="size-3.5" /> {t('numbers.connect')}</Button>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost" className="px-2" aria-label={t('numbers.manage')}><MoreVertical className="size-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem className="gap-2" onClick={() => archive(i)}><Archive className="size-4" /> {t('numbers.archive')}</DropdownMenuItem>
                          <DropdownMenuItem className="gap-2" onClick={() => setClearing(i)}><Eraser className="size-4" /> {t('numbers.clearSession')}</DropdownMenuItem>
                          <DropdownMenuItem className="gap-2 text-destructive focus:text-destructive" onClick={() => setRemoving(i)}><Trash2 className="size-4" /> {t('numbers.delete')}</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </CardContent>
      </Card>

      {/* Arquivados — inativos, reativáveis */}
      {(archived ?? []).length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('numbers.archived')}</p>
          <Card>
            <CardContent className="p-0">
              <ul className="divide-y">
                {(archived ?? []).map((i) => (
                  <li key={i.id} className="flex flex-col gap-2 px-5 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-muted-foreground">{i.nickname || formatPhone(i.phone)}</p>
                      <p className="truncate text-xs text-muted-foreground">{formatPhone(i.phone)}</p>
                    </div>
                    <div className="flex items-center gap-2 sm:justify-end">
                      <Badge variant="secondary">{t('numbers.archivedBadge')}</Badge>
                      <Button size="sm" variant="outline" onClick={() => unarchive(i)}><ArchiveRestore className="size-3.5" /> {t('numbers.reactivate')}</Button>
                    </div>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          <p className="text-xs text-muted-foreground">{t('numbers.archivedHint')}</p>
        </div>
      )}

      {/* remover: arquivar (preserva histórico) vs excluir (definitivo, só sem histórico) */}
      <Dialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('numbers.removeTitle', { name: removing?.nickname || formatPhone(removing?.phone ?? '') })}</DialogTitle>
            <DialogDescription>{t('numbers.removeDesc')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-1 text-sm">
            <div className="rounded-lg border bg-muted/30 p-3">
              <p className="flex items-center gap-2 font-medium"><Archive className="size-4 text-muted-foreground" /> {t('numbers.archive')}</p>
              <p className="mt-1 text-muted-foreground">{t('numbers.archiveExplain')}</p>
            </div>
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3">
              <p className="flex items-center gap-2 font-medium text-destructive"><Trash2 className="size-4" /> {t('numbers.delete')}</p>
              <p className="mt-1 text-muted-foreground">{t('numbers.deleteExplain')}</p>
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button variant="outline" onClick={() => removing && archive(removing)}><Archive className="size-4" /> {t('numbers.archive')}</Button>
            <Button variant="destructive" disabled={deleting} onClick={() => removing && remove(removing)}>
              {deleting && <Spinner className="border-white/40 border-t-white" />}<Trash2 className="size-4" /> {t('numbers.confirmDelete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* limpar sessão: apaga o device pareado → próximo /connect emite QR limpo */}
      <Dialog open={!!clearing} onOpenChange={(o) => !o && setClearing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('numbers.clearSessionTitle', { name: clearing?.nickname || formatPhone(clearing?.phone ?? '') })}</DialogTitle>
            <DialogDescription>{t('numbers.clearSessionDesc')}</DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
            <p className="flex items-center gap-2 font-medium text-destructive"><Eraser className="size-4" /> {t('numbers.clearSession')}</p>
            <p className="mt-1 text-muted-foreground">{t('numbers.clearSessionExplain')}</p>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button variant="outline" onClick={() => setClearing(null)}>{t('common.cancel')}</Button>
            <Button variant="destructive" disabled={clearingBusy} onClick={() => clearing && clearSession(clearing)}>
              {clearingBusy && <Spinner className="border-white/40 border-t-white" />}<Eraser className="size-4" /> {t('numbers.confirmClearSession')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* conectar (QR + SSE) */}
      <ConnectDialog id={connectId} onClose={(connected) => { setConnectId(null); if (connected) refresh(); }} />
    </div>
  );
}

function ConnectDialog({ id, onClose }: { id: string | null; onClose: (connected: boolean) => void }) {
  const t = useT();
  const statusLabel = useStatusLabel();
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [status, setStatus] = useState('connecting');
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!id) { setQrDataUrl(null); setStatus('connecting'); return; }
    let cancelled = false;
    const render = async (code: string) => {
      const url = await QRCode.toDataURL(code, { width: 240, margin: 1 });
      if (!cancelled) setQrDataUrl(url);
    };
    // 1) inicia a conexão → QR inicial
    makeClient().POST('/instances/{id}/connect', { params: { path: { id }, query: { method: 'qr' } } })
      .then(({ data }) => {
        const code = (data as { qr_code?: string })?.qr_code;
        if (code) render(code);
      });
    // 2) SSE: QR atualizado + status ao vivo
    const es = new EventSource(streamUrl(id));
    esRef.current = es;
    es.addEventListener('qr_code', (e) => {
      const code = (JSON.parse((e as MessageEvent).data) as { code?: string }).code;
      if (code) render(code);
    });
    es.addEventListener('instance.status', (e) => {
      const s = (JSON.parse((e as MessageEvent).data) as { status?: string }).status ?? '';
      setStatus(s);
      // Número novo pareia direto em "warming" (limites de envio ainda subindo), nunca "connected":
      // ambos significam pareado com sucesso, então damos o retorno nos dois.
      if (s === 'connected' || s === 'warming') { track('number_connected', { method: 'qr', status: s }); toast.success(t('numbers.connectedToast')); es.close(); onClose(true); }
    });
    return () => { cancelled = true; es.close(); esRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose(status === 'connected' || status === 'warming')}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('numbers.connectTitle')}</DialogTitle>
          <DialogDescription>{t('numbers.connectDesc')}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-2">
          {qrDataUrl ? (
            <img src={qrDataUrl} alt="QR Code" className="rounded-lg border bg-white p-2" />
          ) : (
            <div className="flex h-[240px] w-[240px] items-center justify-center rounded-lg border"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>
          )}
          <Badge variant={status === 'connected' || status === 'warming' ? 'success' : 'warning'}>{statusLabel(status)}</Badge>
        </div>
      </DialogContent>
    </Dialog>
  );
}
